import { ActionIcon, Avatar, Box, Button, Group, Indicator, Menu, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconBell } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@workos-inc/authkit-react';
import type { HubNotification, WaitingRequest } from '../api/notifications';
import { useNotifications, useNotificationsPolling } from '../hooks/useNotifications';
import { getInitials } from '../lib/initials';
import { PANEL_WAITING_LIMIT, canSeeWaiting, overflowLine, updateLine, waitingLine } from '../lib/notifications';
import { profilePath } from '../lib/profileNav';
import { relativeTimeFrom } from '../lib/relativeTime';
import { ENTERPRISE_URL } from '../lib/scenarioNav';

// Plain module fn (outside render) so the clock read stays lint-pure.
function nowForRender(): Date { return new Date(); }

const requestsPath = profilePath('requests');

export function NotificationsBell() {
  useNotificationsPolling();
  const auth = useAuth();
  const { state, unread, markRead, markAllRead } = useNotifications();
  const now = nowForRender();

  const waiting = canSeeWaiting(auth.permissions) ? (state?.waiting ?? []) : [];
  const updates = state?.updates ?? [];
  const shown = waiting.slice(0, PANEL_WAITING_LIMIT);
  const hidden = waiting.length - shown.length;
  const anyUnreadUpdate = updates.some((u) => !u.readAt);
  const label = unread > 0 ? `Notifications, ${unread} unread` : 'Notifications';

  return (
    <Menu width={380} position="bottom-end" offset={10} withArrow={false} shadow="lg">
      <Menu.Target>
        <Tooltip label="Notifications" position="bottom" withArrow>
          <Indicator label={unread} size={18} offset={4} disabled={unread === 0} color="var(--accent-text)" inline>
            <ActionIcon
              variant="subtle"
              aria-label={label}
              size={34}
              style={{
                border: '1px solid var(--border-default)',
                '--ai-color': unread > 0 ? 'var(--accent-text)' : 'var(--text-muted)',
                '--ai-hover-color': 'var(--accent-text)',
                '--ai-hover': 'rgba(81,100,112,0.03)',
              }}
            >
              <IconBell size={17} stroke={2} />
            </ActionIcon>
          </Indicator>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', padding: 0 }}>
        <Group justify="space-between" px={16} py={12} style={{ borderBottom: '1px solid var(--border-default)' }}>
          <Text fw={700} fz="sm">Notifications</Text>
          {anyUnreadUpdate && (
            <Button variant="subtle" size="compact-xs" onClick={() => void markAllRead()}>Mark all as read</Button>
          )}
        </Group>

        <Box data-testid="notifications-list" mah="70vh" style={{ overflowY: 'auto' }}>
        {shown.length > 0 && (
          <Stack gap={0}>
            <Text px={16} pt={10} pb={4} fz={10} fw={700} tt="uppercase" lts={0.6} c="var(--text-subtle)">Waiting for you</Text>
            {shown.map((w) => <WaitingRow key={w.id} item={w} now={now} />)}
            {hidden > 0 && (
              <Box px={16} py={10} pl={60} style={{ borderBottom: '1px solid var(--border-default)' }}>
                <Text component={Link} to={requestsPath} fz="xs" fw={600} c="var(--accent-text)" td="none">{overflowLine(hidden)}</Text>
              </Box>
            )}
          </Stack>
        )}

        {updates.map((u) => <UpdateRow key={u.id} item={u} now={now} onRead={() => void markRead(u.id)} />)}

        {shown.length === 0 && updates.length === 0 && (
          <Text px={16} py={14} fz="sm" c="var(--text-muted)">Nothing new.</Text>
        )}
        </Box>
      </Menu.Dropdown>
    </Menu>
  );
}

function WaitingRow({ item, now }: Readonly<{ item: WaitingRequest; now: Date }>) {
  return (
    <Group gap={12} px={16} py={10} wrap="nowrap" align="center" style={{ borderBottom: '1px solid var(--border-default)' }}>
      <Avatar size={32} radius="xl" style={{ '--avatar-bg': 'linear-gradient(135deg,#516470,#0D2839)', '--avatar-bd': 'none' }}>
        <Text size="xs" fw={700} c="white">{getInitials(item.requesterName ?? '?')}</Text>
      </Avatar>
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Text fz="sm">{waitingLine(item)}</Text>
        <Text fz="xs" c="var(--text-subtle)">{relativeTimeFrom(item.requestedAt, now)}</Text>
      </Stack>
      <Button component={Link} to={requestsPath} size="compact-sm" variant="light">Review</Button>
    </Group>
  );
}

function UpdateRow({ item, now, onRead }: Readonly<{ item: HubNotification; now: Date; onRead: () => void }>) {
  const isUnread = !item.readAt;
  const line = updateLine(item);
  const body = (
    <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
      <Text fz="sm" c={isUnread ? undefined : 'var(--text-muted)'}>{line}</Text>
      {item.reason && (
        <Box px={10} py={8} style={{ background: 'var(--bg-subtle)', borderRadius: 8 }}>
          <Text fz="xs" style={{ whiteSpace: 'pre-wrap' }}>{item.reason}</Text>
          {item.actorName && <Text fz="xs" c="var(--text-subtle)" mt={2}>{item.actorName}</Text>}
        </Box>
      )}
      <Text fz="xs" c="var(--text-subtle)">{relativeTimeFrom(item.createdAt, now)}</Text>
    </Stack>
  );
  return (
    <Group
      gap={12} px={16} py={12} wrap="nowrap" align="flex-start" data-unread={isUnread ? 'true' : 'false'}
      style={{ borderBottom: '1px solid var(--border-default)', background: isUnread ? 'var(--brand-soft)' : undefined, cursor: isUnread ? 'pointer' : undefined }}
      onClick={isUnread ? onRead : undefined}
    >
      <Box w={8} h={8} mt={6} style={{ flex: 'none', borderRadius: 999, background: isUnread ? 'var(--accent-text)' : 'transparent' }} />
      {isUnread
        ? <UnstyledButton aria-label={`Mark read: ${line}`} onClick={onRead} style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>{body}</UnstyledButton>
        : body}
      {item.kind === 'access_granted' && (
        <Button component={Link} to={ENTERPRISE_URL} size="compact-sm" variant="light" onClick={onRead}>Open</Button>
      )}
    </Group>
  );
}
